import { Navigate, Outlet, type RouteObject } from 'react-router-dom'
import { CodemaxBrand } from '@/components/Brand'
import { PortalShell } from '@/components/PortalShell'
import { Spinner } from '@/components/ui/primitives'
import { ToastProvider } from '@/components/ui/toast'
import { AuthProvider, useAuth } from '@/lib/auth'
import { Company } from '@/routes/Company'
import { Deals } from '@/routes/Deals'
import { Home } from '@/routes/Home'
import { NotFound } from '@/routes/NotFound'
import { Payouts } from '@/routes/Payouts'
import { RateCard } from '@/routes/RateCard'
import { Statement } from '@/routes/Statement'
import { Statements } from '@/routes/Statements'
import { SignedOut } from '@/routes/SignedOut'
import { SignIn } from '@/routes/SignIn'

/**
 * The portal's routes.
 *
 * Six destinations and one detail page. Home holds the index because it answers the
 * question a partner opens the portal with - what have I been paid - and Deals is the
 * input to that rather than the answer. Rate Card comes before both: it is the only screen
 * used before a deal exists, and everything after it is the record of one that already does.
 *
 * There is no anonymous route here at all: the two
 * that exist in this system - customer acceptance and acceptance verification - belong to
 * the staff application, and a partner signs in through ARMS like everybody else.
 *
 * The shape mirrors the questions asked in order: are the providers up, has the session
 * been restored, is anybody signed in.
 */
export const routes: RouteObject[] = [
  {
    element: <Providers />,
    children: [
      {
        element: <Booting />,
        children: [
          {
            element: <PublicOnly />,
            children: [
              { path: 'sign-in', element: <SignIn /> },
              { path: 'signed-out', element: <SignedOut /> },
            ],
          },
          {
            element: <RequireAuth />,
            children: [
              {
                element: <PortalShell />,
                children: [
                  { index: true, element: <Home /> },
                  { path: 'rate-card', element: <RateCard /> },
                  { path: 'deals', element: <Deals /> },
                  { path: 'statements', element: <Statements /> },
                  { path: 'statements/:id', element: <Statement /> },
                  { path: 'payouts', element: <Payouts /> },
                  { path: 'company', element: <Company /> },
                  { path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]

function Providers() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </ToastProvider>
  )
}

/**
 * Nothing renders until the session has been checked. Without this the sign-in screen
 * flashes on every reload for somebody who is already signed in.
 *
 * A bare spinner on an empty page is indistinguishable from a page that failed to load,
 * so this says whose application is starting and that it is still working.
 */
function Booting() {
  const { restoring } = useAuth()

  if (!restoring) return <Outlet />

  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="flex flex-col items-center gap-6">
        <CodemaxBrand className="h-9" />

        <div role="status" className="flex items-center gap-2.5 text-muted">
          <Spinner className="size-4" label={null} />
          <span className="text-[13px]">Loading...</span>
        </div>
      </div>
    </div>
  )
}

function RequireAuth() {
  const { partner } = useAuth()

  return partner ? <Outlet /> : <Navigate to="/sign-in" replace />
}

/** The two screens that only mean something to somebody who is not signed in. */
function PublicOnly() {
  const { partner } = useAuth()

  return partner ? <Navigate to="/" replace /> : <Outlet />
}
